/**
 * Iter 230 — Dashboard "Automations en cours" pour le Hub Marketing.
 *
 * Affiche en temps réel :
 *  - Workflows actifs ayant tourné aujourd'hui
 *  - Leads en délai d'attente (delay actions)
 *  - Emails IA envoyés aujourd'hui
 *  - ROI global (revenue estimé - cost emails/IA)
 *  - Tendance 7 jours
 *  - Top 5 workflows les plus actifs
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  Zap, Clock, Mail, TrendingUp, AlertCircle, ChevronRight,
  Loader2, RefreshCw, Settings as SettingsIcon, Bot, DollarSign,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export default function AutomationsDashboard() {
  const navigate = useNavigate();
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingDealValue, setEditingDealValue] = useState(false);
  const [dealValue, setDealValue] = useState(250);
  const [savingDeal, setSavingDeal] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketing/automations/dashboard");
      setData(r.data);
      setDealValue(r.data?.roi?.avg_deal_value_eur || 250);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { reload(); }, [reload]);

  // Auto-refresh toutes les 30s
  useEffect(() => {
    const id = setInterval(reload, 30000);
    return () => clearInterval(id);
  }, [reload]);

  const saveDealValue = async () => {
    setSavingDeal(true);
    try {
      await api.post("/marketing/automations/avg-deal-value", { avg_deal_value: dealValue });
      setEditingDealValue(false);
      await reload();
    } catch (e) {
      alert("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingDeal(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-8 flex items-center justify-center" data-testid="automations-loading">
        <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-6 text-center text-slate-500 text-sm">
        Aucune donnée d'automation disponible.
      </div>
    );
  }

  const { summary, roi, top_workflows, trend_7d } = data;
  const maxRuns = Math.max(...(trend_7d || []).map((d) => d.runs), 1);
  const roiPositive = roi.roi_eur >= 0;

  return (
    <div className="space-y-4" data-testid="automations-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-400" /> Automations en cours
          </h2>
          <p className="text-[11px] text-slate-500">Actualisé toutes les 30 secondes · Données du jour (UTC)</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={reload} className="text-slate-500 hover:text-white p-1" title="Rafraîchir maintenant">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <Button onClick={() => navigate("/admin/workflows")} variant="outline" size="sm" className="border-white/10 text-white hover:bg-white/5 text-[11px] h-8" data-testid="btn-manage-workflows">
            Gérer les workflows <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </div>

      {/* KPIs en grille */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          icon={Zap}
          color="#a855f7"
          label="Workflows actifs"
          value={summary.active_workflows_count}
          sub={`${summary.runs_today} runs aujourd'hui`}
          dataTestid="kpi-active-workflows"
        />
        <KpiCard
          icon={Clock}
          color="#f59e0b"
          label="Leads en attente"
          value={summary.leads_in_delay}
          sub="dans un délai"
          dataTestid="kpi-leads-delay"
        />
        <KpiCard
          icon={Mail}
          color="#3b82f6"
          label="Emails IA envoyés"
          value={summary.ai_emails_today + (summary.meta_ai_messages_today || 0)}
          sub={`${summary.meta_ai_messages_today || 0} via Meta`}
          dataTestid="kpi-ai-emails"
        />
        <KpiCard
          icon={Bot}
          color="#22c55e"
          label="Conversions auto"
          value={summary.conversions_today}
          sub="leads convertis"
          dataTestid="kpi-conversions"
        />
      </div>

      {/* ROI Card */}
      <div className={`bg-gradient-to-br ${roiPositive ? "from-emerald-500/10 to-blue-500/5 border-emerald-500/30" : "from-rose-500/10 to-orange-500/5 border-rose-500/30"} border rounded-xl p-5`} data-testid="roi-card">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <DollarSign className={`w-5 h-5 ${roiPositive ? "text-emerald-400" : "text-rose-400"}`} />
            <h3 className="text-sm font-bold text-white">ROI global aujourd'hui</h3>
          </div>
          {!editingDealValue ? (
            <button
              onClick={() => setEditingDealValue(true)}
              className="text-[10px] text-slate-400 hover:text-white inline-flex items-center gap-0.5"
              data-testid="btn-edit-deal"
            >
              <SettingsIcon className="w-3 h-3" /> Configurer valeur deal
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={dealValue}
                onChange={(e) => setDealValue(parseFloat(e.target.value) || 0)}
                className="bg-[#0d0a14] border-white/10 text-white text-xs h-7 w-24"
                data-testid="input-deal-value"
              />
              <span className="text-[10px] text-slate-500">€</span>
              <Button onClick={saveDealValue} disabled={savingDeal} size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white h-7 text-[11px] px-2" data-testid="btn-save-deal">
                {savingDeal ? <Loader2 className="w-3 h-3 animate-spin" /> : "OK"}
              </Button>
              <button onClick={() => setEditingDealValue(false)} className="text-[10px] text-slate-400 hover:text-white">Annuler</button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <div className="text-[10px] uppercase text-slate-400 mb-0.5">Revenue estimé</div>
            <div className="text-2xl font-black text-emerald-400">{roi.revenue_estimated_eur.toFixed(0)} €</div>
            <div className="text-[9px] text-slate-500">{summary.conversions_today} × {roi.avg_deal_value_eur.toFixed(0)}€</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-400 mb-0.5">Coût emails</div>
            <div className="text-lg font-bold text-blue-300">{roi.cost_emails_eur.toFixed(2)} €</div>
            <div className="text-[9px] text-slate-500">~{summary.ai_emails_today + (summary.meta_ai_messages_today || 0)} envois</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-400 mb-0.5">Coût IA</div>
            <div className="text-lg font-bold text-violet-300">{roi.cost_ia_eur.toFixed(2)} €</div>
            <div className="text-[9px] text-slate-500">{summary.ia_calls_today} appels Gemini</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-400 mb-0.5">ROI net</div>
            <div className={`text-2xl font-black ${roiPositive ? "text-emerald-400" : "text-rose-400"}`}>
              {roiPositive ? "+" : ""}{roi.roi_eur.toFixed(0)} €
            </div>
            <div className="text-[9px] text-slate-500">{roi.roi_pct > 0 ? "+" : ""}{roi.roi_pct}%</div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Trend 7d */}
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 lg:col-span-2" data-testid="trend-card">
          <h3 className="text-xs font-bold uppercase text-slate-400 mb-3 flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5" /> Activité 7 derniers jours
          </h3>
          {(!trend_7d || trend_7d.length === 0) ? (
            <p className="text-[11px] text-slate-500">Aucun run récent</p>
          ) : (
            <div className="flex items-end justify-between gap-1 h-32">
              {trend_7d.map((d) => {
                const h = (d.runs / maxRuns) * 100;
                const isToday = trend_7d.indexOf(d) === trend_7d.length - 1;
                return (
                  <div key={d.day} className="flex-1 flex flex-col items-center justify-end gap-1">
                    <div
                      className={`w-full rounded-t transition-all ${isToday ? "bg-violet-500" : "bg-violet-500/40"}`}
                      style={{ height: `${Math.max(h, 2)}%` }}
                      title={`${d.day} : ${d.runs} runs`}
                    />
                    <div className="text-[9px] text-slate-500">{d.day.slice(5)}</div>
                    <div className={`text-[10px] font-bold ${isToday ? "text-violet-300" : "text-slate-400"}`}>{d.runs}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top workflows */}
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid="top-workflows">
          <h3 className="text-xs font-bold uppercase text-slate-400 mb-3">Top workflows aujourd'hui</h3>
          {(!top_workflows || top_workflows.length === 0) ? (
            <p className="text-[11px] text-slate-500">Aucun workflow déclenché aujourd'hui</p>
          ) : (
            <ul className="space-y-2">
              {top_workflows.map((w) => (
                <li key={w.workflow_id} className="flex items-center justify-between gap-2 p-2 rounded hover:bg-white/5">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-white truncate">{w.name}</div>
                    <div className="text-[9px] text-slate-500 truncate">{w.trigger_type}</div>
                  </div>
                  <span className="text-xs font-bold text-violet-300 flex-shrink-0">{w.runs_today}×</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Status indicators */}
      {summary.runs_failed > 0 && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3 flex items-center gap-2" data-testid="failures-alert">
          <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
          <div className="flex-1">
            <span className="text-xs text-rose-300">
              {summary.runs_failed} run(s) en échec aujourd'hui — vérifiez l'historique d'exécution
            </span>
          </div>
          <button onClick={() => navigate("/admin/workflows")} className="text-[10px] text-rose-400 hover:underline">
            Voir →
          </button>
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, color, label, value, sub, dataTestid }) {
  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-3" data-testid={dataTestid}>
      <Icon className="w-4 h-4 mb-1.5" style={{ color }} />
      <div className="text-2xl font-black" style={{ color }}>{value}</div>
      <div className="text-[10px] text-slate-500">{label}</div>
      {sub && <div className="text-[9px] text-slate-600 mt-0.5">{sub}</div>}
    </div>
  );
}
